import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Loader2 } from "lucide-react";
import { CleanupFamilyTransfersButton } from "./CleanupFamilyTransfersButton";
import { backupRecordsToExcel } from "@/utils/countyResearchBackup";

interface ShermanDocument {
  documentNumber: string;
  grantors: string[];
  grantees: string[];
  docType: string;
  recordedDate: string;
  lenderName: string;
}

interface Props {
  onRecordsSaved: () => void;
}

const CORPORATE_KEYWORDS = [
  'BANK', 'MORTGAGE', 'LENDING', 'CREDIT UNION', 'FINANCE', 'FUNDING',
  'LLC', 'LTD', 'INC', 'CORP', 'CORPORATION', 'FEDERAL', 'NATIONAL',
  'SERVICES', 'SOLUTIONS', 'CAPITAL', 'INVESTMENTS', 'HOLDINGS',
  'WELLS FARGO', 'CHASE', 'PENNYMAC', 'DHI', 'USAA', 'NAVY FEDERAL',
  'VETERANS UNITED', 'GUILD', 'CHURCHILL', 'FAIRWAY', 'NEWREZ',
  'BENCHMARK', 'SWBC', 'CANOPY', 'FLAGSTAR', 'FIDELITY',
  'FIRST NATIONAL', 'FIRST FINANCIAL', 'FIRST CONTINENTAL',
  'FIRST UNITED', 'FIRST STATE', 'FIRST COMMUNITY',
  'PLANET HOME', 'VILLAGE CAPITAL', 'ANGEL OAK',
  'UNITED WHOLESALE', 'SENTE', 'LOAN STORE',
  'HOUSING & URBAN', 'HUD', 'SECRETARY OF HOUSING',
  'MORTGAGE RESEARCH CENTER', 'MORTGAGE SOLUTIONS',
  'READYCAP', 'ROCKET MORTGAGE', 'PENNYMAC LOAN', 'PILGRIM MORTGAGE',
  'EFINITY FINANCIAL', 'CASCADE FINANCIAL', 'SOUTHWEST STAGE',
  'GUILD MORTGAGE', 'REVOLUTION MORTGAGE', 'CMG MORTGAGE', 'CMG HOME',
  'HOMETRUST MORTGAGE', 'ROUNDPOINT MORTGAGE',
  'PROSPERITY BANK', 'CENTENNIAL BANK', 'HAPPY STATE BANK',
  'PANHANDLE FIRST', 'ANCHOR D BANK', 'FRONTIER BANK',
  'SUNDOWN STATE BANK', 'EQUITY BANK', 'EQUITY PRIME',
  'TEXASBANK', 'AMARILLO NATIONAL', 'CITIZENS BANK', 'PEOPLES BANK',
  'FARM CREDIT', 'AGTRUST', 'AGTEXAS', 'PLAINS LAND BANK',
  'CAPITAL FARM CREDIT', 'CULTIVATED LENDING',
  'CONTERRA AGRICULTURAL', 'CENTRAL TEXAS FARM CREDIT',
  'ALLIANCE CREDIT UNION', 'ACCESS COMMUNITY CREDIT',
  'EDUCATION CREDIT', 'CITIBANK', 'JPMORGAN',
  'PROVIDENCE HOME LOANS', 'RIGHT START MORTGAGE',
  'NATIONS RELIABLE LENDING', 'LOANUNITED',
  'MORTGAGE ELECTRONIC REGISTRATION', 'MERS',
  'DEPARTMENT OF HOUSING', 'SERVICING CENTER',
  'HALL AND HALL MORTGAGE', 'BLACK MANN AND GRAHAM',
  'LAND TITLE RESOURCES', 'PETROSWISS',
  'PLA SERVICES',
];

export function ShermanCountyTextParser({ onRecordsSaved }: Props) {
  const [textData, setTextData] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const { toast } = useToast();

  const parseShermanData = (text: string): ShermanDocument[] => {
    const lines = text.trim().split('\n');
    const documents: ShermanDocument[] = [];

    let currentDocNum = '';
    let currentRole: '1' | '2' | null = null;
    let pendingGrantors: string[] = [];
    let pendingGrantees: string[] = [];
    let currentDocType = '';
    let currentDate = '';

    const flushDoc = () => {
      if (!currentDocNum) return;
      documents.push({
        documentNumber: currentDocNum,
        grantors: [...pendingGrantors],
        grantees: [...pendingGrantees],
        docType: currentDocType,
        recordedDate: currentDate,
        lenderName: '',
      });
    };

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;

      // Doc number line: "0378 0241" or "0378 0241 +" (book page format)
      const docMatch = line.match(/^(\d{4}\s+\d{4})\s*\+?\s*$/);
      if (docMatch) {
        flushDoc();
        currentDocNum = docMatch[1].replace(/\s+/g, ' ');
        currentRole = null;
        pendingGrantors = [];
        pendingGrantees = [];
        currentDocType = '';
        currentDate = '';
        continue;
      }

      // Role marker: standalone "1" or "2"
      if (line === '1' || line === '2') {
        currentRole = line as '1' | '2';
        continue;
      }

      // Date line (standalone MM/DD/YYYY)
      if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(line)) {
        currentDate = line;
        continue;
      }

      // Doc type line
      if (/^(DEED OF TRUST|VENDOR|CONTRACT FOR DEED|LAND CONTRACT|WARRANTY DEED|SPECIAL WARRANTY|QUIT CLAIM|ASSIGNMENT|RELEASE)/i.test(line)) {
        const dateMatch = line.match(/(\d{1,2}\/\d{1,2}\/\d{4})\s*$/);
        if (dateMatch) {
          currentDate = dateMatch[1];
          currentDocType = line.substring(0, line.indexOf(dateMatch[1])).trim();
        } else {
          currentDocType = line;
        }
        continue;
      }

      // If we have a current role and doc, this line is a name
      if (currentRole && currentDocNum) {
        const cleanName = line.replace(/\s*\+\s*$/, '').trim();
        if (cleanName.length > 0) {
          if (currentRole === '1') {
            pendingGrantors.push(cleanName);
          } else {
            pendingGrantees.push(cleanName);
          }
        }
        continue;
      }
    }

    // Flush last document
    flushDoc();

    // Identify primary lender for each doc
    for (const doc of documents) {
      for (const grantee of doc.grantees) {
        const upper = grantee.toUpperCase();
        if (upper.includes('TRUSTEE') || upper.includes(', TR') || upper.includes(' TR ') || upper.endsWith(' TR')) continue;
        if (!doc.lenderName) {
          doc.lenderName = grantee;
        }
      }
      if (!doc.lenderName && doc.grantees.length > 0) {
        doc.lenderName = doc.grantees[0];
      }
    }

    // Sort by date descending
    documents.sort((a, b) => {
      const parseDate = (d: string) => {
        if (!d) return 0;
        const parts = d.split('/');
        if (parts.length === 3) return new Date(`${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`).getTime();
        return 0;
      };
      return parseDate(b.recordedDate) - parseDate(a.recordedDate);
    });

    return documents;
  };

  const isCorpLender = (name: string): boolean => {
    const upper = name.toUpperCase();
    return CORPORATE_KEYWORDS.some(kw => upper.includes(kw));
  };

  const isFamilyTransfer = (doc: ShermanDocument): boolean => {
    const getLastName = (name: string) => name.split(/\s+/)[0]?.toUpperCase() || '';
    const grantorLastNames = doc.grantors.map(getLastName).filter(n => n.length > 1);
    const granteeLastNames = doc.grantees.map(getLastName).filter(n => n.length > 1);
    return grantorLastNames.some(gln => granteeLastNames.some(eln => gln === eln));
  };

  const handleProcess = async () => {
    if (!textData.trim()) {
      toast({ title: "No data", description: "Please paste records", variant: "destructive" });
      return;
    }

    setIsProcessing(true);
    try {
      const documents = parseShermanData(textData);
      console.log(`Parsed ${documents.length} total Sherman County documents`);

      if (documents.length === 0) {
        toast({ title: "No records found", description: "Could not parse any records from the pasted data.", variant: "destructive" });
        setIsProcessing(false);
        return;
      }

      // Only keep relevant doc types (DOT, Vendor's Lien, Contract for Deed)
      const deedDocs = documents.filter(d => {
        const upper = d.docType.toUpperCase();
        return upper.includes('DEED OF TRUST') || upper.includes('VENDOR') ||
               upper.includes('CONTRACT FOR DEED') || upper.includes('LAND CONTRACT');
      });

      const familyCount = deedDocs.filter(isFamilyTransfer).length;
      const nonFamily = deedDocs.filter(d => !isFamilyTransfer(d));

      const corporateCount = nonFamily.filter(d => d.lenderName && isCorpLender(d.lenderName)).length;
      const potentialOwnerFinanced = nonFamily.filter(d => {
        if (!d.lenderName) return false;
        return !isCorpLender(d.lenderName);
      });

      console.log(`Deeds: ${deedDocs.length}, Family: ${familyCount}, Corporate: ${corporateCount}, Potential OF: ${potentialOwnerFinanced.length}`);

      if (potentialOwnerFinanced.length === 0) {
        toast({
          title: "No owner-financed deals found",
          description: `Analyzed ${documents.length} documents — ${deedDocs.length} deeds, ${familyCount} family transfers, ${corporateCount} corporate lenders`,
        });
        setTextData("");
        setIsProcessing(false);
        return;
      }

      // Send to AI for final verification
      const deedText = potentialOwnerFinanced.map(d =>
        `Document: ${d.documentNumber}\n` +
        `Date: ${d.recordedDate}\n` +
        `Grantor(s): ${d.grantors.join(', ')}\n` +
        `Grantee/Beneficiary: ${d.lenderName}\n` +
        `All Grantees: ${d.grantees.join(', ')}\n`
      ).join('\n---\n');

      const { data: filterData, error: filterError } = await supabase.functions.invoke(
        'filter-owner-finance',
        { body: { deedText } }
      );

      if (filterError) throw filterError;

      const ownerFinancedDeals = filterData?.filteredDeals || [];
      const ownerFinancedDocNumbers = new Set(
        ownerFinancedDeals.map((deal: any) => deal.documentNumber)
      );

      const confirmedRecords = potentialOwnerFinanced.filter(d =>
        ownerFinancedDocNumbers.has(d.documentNumber)
      );

      console.log(`AI confirmed ${confirmedRecords.length} owner-financed deals`);

      if (confirmedRecords.length === 0) {
        toast({
          title: "No owner-financed deals confirmed",
          description: `Hard filter passed ${potentialOwnerFinanced.length}, but AI rejected all as non-individual lenders`,
        });
        setTextData("");
        setIsProcessing(false);
        return;
      }

      // Check for existing records
      const existingQuery = await supabase
        .from('county_research')
        .select('document_number')
        .eq('county', 'sherman')
        .in('document_number', confirmedRecords.map(r => r.documentNumber));

      const existingDocs = new Set(existingQuery.data?.map(d => d.document_number) || []);
      const newRecords = confirmedRecords.filter(r => !existingDocs.has(r.documentNumber));

      if (newRecords.length === 0) {
        toast({
          title: "No new records",
          description: `Found ${confirmedRecords.length} owner-financed deals, but all already exist in database`,
        });
        setTextData("");
        setIsProcessing(false);
        return;
      }

      const parseDate = (dateStr: string): string => {
        if (!dateStr) return new Date().toISOString().split('T')[0];
        const parts = dateStr.split('/');
        if (parts.length === 3) {
          return `${parts[2]}-${parts[0].padStart(2, '0')}-${parts[1].padStart(2, '0')}`;
        }
        return new Date().toISOString().split('T')[0];
      };

      // For Deeds of Trust: "1" = Borrower (grantor_name in TX = grantee_name display)
      // "2" = Lender/Beneficiary (grantee_name in TX = grantor_name display)
      const recordsToInsert = newRecords.map(record => ({
        county: 'sherman',
        document_number: record.documentNumber,
        filing_date: parseDate(record.recordedDate),
        document_type: record.docType,
        grantor_name: record.grantees.join('; '),
        grantee_name: record.grantors.join('; '),
        is_potential_owner_finance: true,
        confidence_score: 80,
        detection_reasons: ['AI confirmed individual beneficiary'],
        status: 'new',
        raw_data: {
          grantors: record.grantors,
          grantees: record.grantees,
        } as any,
      }));

      const { error: insertError } = await supabase
        .from('county_research')
        .upsert(recordsToInsert, { onConflict: 'document_number', ignoreDuplicates: false });

      if (insertError) throw insertError;

      backupRecordsToExcel(recordsToInsert, 'sherman', 'sherman_parser');

      toast({
        title: "Success!",
        description: `Saved ${newRecords.length} owner-financed deals!\n` +
          `Total: ${documents.length} | Deeds: ${deedDocs.length} | Family: ${familyCount} | Corporate: ${corporateCount} | ` +
          `AI confirmed: ${confirmedRecords.length} | New saved: ${newRecords.length}`,
      });

      setTextData("");
      onRecordsSaved();
    } catch (error) {
      console.error('Error processing Sherman County data:', error);
      toast({ title: "Error", description: "Failed to process records. Please try again.", variant: "destructive" });
    } finally {
      setIsProcessing(false);
    }
  };

  const previewCount = textData.trim() ? parseShermanData(textData).length : 0;

  return (
    <Card>
      <CardHeader>
        <CardTitle>Sherman County, TX - Kofile Text Parser with AI Filtering</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-2">
          <p className="text-sm text-muted-foreground">
            Paste deed of trust records from the Sherman County PublicSearch portal.
            Uses the standard Kofile vertical format with "1" (Borrower) and "2" (Lender) markers.
          </p>
          <p className="text-sm text-muted-foreground">
            Records are filtered to remove corporate lenders and family transfers, then AI-verified.
          </p>
        </div>

        <Textarea
          value={textData}
          onChange={(e) => setTextData(e.target.value)}
          placeholder={`Paste Sherman County deed of trust records here...\n\nExample format:\n0378 0241 +\n\n1\n\nSTRATFORD TEXAS HOTEL LLC\n\n2\n\nFRONTIER BANK\n\nDEED OF TRUST\n\n12/31/2025`}
          className="min-h-[300px] font-mono text-sm"
        />

        {previewCount > 0 && (
          <p className="text-sm text-muted-foreground">
            Detected <strong>{previewCount}</strong> unique documents to analyze
          </p>
        )}

        <div className="flex gap-2">
          <Button
            onClick={handleProcess}
            disabled={isProcessing || !textData.trim()}
            className="flex-1"
          >
            {isProcessing ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Processing with AI...
              </>
            ) : (
              `Process ${previewCount > 0 ? previewCount + ' ' : ''}Records`
            )}
          </Button>

          <CleanupFamilyTransfersButton onCleanupComplete={onRecordsSaved} />
        </div>

        {isProcessing && (
          <p className="text-sm text-muted-foreground text-center">
            Filtering corporate lenders & family transfers, then AI verifying remaining records...
          </p>
        )}
      </CardContent>
    </Card>
  );
}
